import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid2,
  TextField,
  FormControlLabel,
  Switch,
  Snackbar,
  Alert
} from "@mui/material";
import AxiosInstance from "../../utils/Axios.ts";
import LocationInput from "../objects/LocationInput.tsx";
import AgentInput from "../objects/AgentInput.tsx";
import { renderAgentStaffSelection, renderCheckInSelection, renderWeatherSelection, renderWindSelection } from "./ReportForm/Selection.tsx";

interface ReportOverlayProps {
  open: boolean;
  onClose: () => void;
  parentTourId: string;
}

const ReportOverlay: React.FC<ReportOverlayProps> = ({
  open,
  onClose,
  parentTourId,
}) => {
  const [state, setState] = useState({
    parent: Number(parentTourId),
    booking_number: "",
    date: "",
    pier: 0,
    agent: 0,
    ship_name: "",
    tour_type: "",
    arrival_time: "",
    weather: "",
    wind: "",
    agent_staff: "",
    check_in: "",
    delay: false,
    delay_reason: "",
    complaint: false,
    complaint_reason: "",
    compliment: false,
    compliment_reason: "",
    incident: false,
    incident_reason: "",
    comment: "",
  });


  useEffect(() => {
    const fetchParentTour = async () => {
      try {
        const response = await AxiosInstance.get(`/parent/${parentTourId}/`);
        setState((prevState) => ({
          ...prevState,
          parent: Number(parentTourId),
          booking_number: response.data.booking_id,
          date: response.data.date,
          pier: response.data.location,
          agent: response.data.agent,
          ship_name: response.data.name,
          tour_type: response.data.tourType,
          arrival_time: response.data.time_from,

        }));
      } catch (error) {
        setSnackbar({
          open: true,
          message: "Failed to fetch parent data. Please try again.",
          severity: "error",
        });
      }
    };

    if (open) {
      fetchParentTour();
    }
  }, [open, parentTourId]);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error" as "error" | "success",
  });

  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  const handleSubmit = async () => {
    try {
      const { booking_number, ...payload } = state;
      const response = await AxiosInstance.post(
        `/pier_report/create/`,
        payload,
      );

      if (response.status === 201) {
        setSnackbar({
          open: true,
          message: "Report created successfully!",
          severity: "success",
        });
        onClose();
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to create report. Please try again.",
        severity: "error",
      });
      console.error(error);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;
    setState({ ...state, [name]: type === "checkbox" ? checked : value });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth fullScreen={true}>
      <DialogTitle>Pier Day Report</DialogTitle>
      <DialogContent>
        <Grid2 container spacing={2}>
          <Grid2 size={12} marginTop={2}>
            <TextField
              fullWidth
              label="Booking Number"
              name="booking_number"
              value={state.booking_number}
              onChange={handleChange}
              InputProps={{ readOnly: true }}
              required
            />
          </Grid2>
          <Grid2 size={12}>
            <TextField
              fullWidth
              label="Date"
              name="date"
              type="date"
              value={state.date}
              onChange={handleChange}
              required
            />
          </Grid2>
          <Grid2 size={12}>
            <LocationInput
              value={state.pier}
              onChange={(value) => value && setState({ ...state, pier: value.id })}
              />
          </Grid2>
          <Grid2 size={12}>
            <AgentInput
              value={state.agent}
              onChange={(value) => setState({ ...state, agent: value })}
            />
          </Grid2>
          <Grid2 size={12}>
            <TextField
              fullWidth
              label="Ship Name"
              name="ship_name"
              value={state.ship_name}
              onChange={handleChange}
            />
          </Grid2>
          <Grid2 size={12}>
            <TextField
              fullWidth
              label="Arrival Time"
              name="arrival_time"
              type="time"
              value={state.arrival_time}
              onChange={handleChange}
            />
          </Grid2>
          <Grid2 size={12}>
            {renderWeatherSelection(state.weather, handleChange)}
          </Grid2>
          <Grid2 size={12}>
            {renderWindSelection(state.wind, handleChange)}
          </Grid2>
          <Grid2 size={12}>
            {renderAgentStaffSelection(state.agent_staff, handleChange)}
          </Grid2>
          <Grid2 size={12}>
            {renderCheckInSelection(state.check_in, handleChange)}
          </Grid2>
          <Grid2 size={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={state.complaint}
                  onChange={(e) => handleChange(e as any)}
                  name="complaint"
                />
              }
              label="Complaint"
            />
            {state.complaint && (
              <TextField
                fullWidth
                label="Complaint Reason"
                name="complaint_reason"
                value={state.complaint_reason}
                onChange={handleChange}
              />
            )}
          </Grid2>
          <Grid2 size={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={state.compliment}
                  onChange={(e) => handleChange(e as any)}
                  name="compliment"
                />
              }
              label="Compliment"
            />
            {state.compliment && (
              <TextField
                fullWidth
                label="Compliment Reason"
                name="compliment_reason"
                value={state.compliment_reason}
                onChange={handleChange}
              />
            )}
          </Grid2>
          <Grid2 size={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={state.delay}
                  onChange={(e) => handleChange(e as any)}
                  name="delay"
                />
              }
              label="Delay"
            />
            {state.delay && (
              <TextField
                fullWidth
                label="Delay Reason"
                name="delay_reason"
                value={state.delay_reason}
                onChange={handleChange}
              />
            )}
          </Grid2>
          <Grid2 size={12}>
            <TextField
              fullWidth
              label="Comment"
              name="comment"
              value={state.comment}
              onChange={handleChange}
            />
          </Grid2>
        </Grid2>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary">
          Submit
        </Button>
      </DialogActions>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ReportOverlay;
